import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Grid,
  Button,
  Avatar,
  Divider,
  IconButton,
  FormControlLabel,
  Switch,
  Snackbar,
  Alert,
} from "@mui/material";
import { useFormik, getIn } from "formik";
import * as Yup from "yup";
import axios from "axios";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import Autocomplete from "@mui/material/Autocomplete";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";
countries.registerLocale(enLocale);

const API_BASE_URL = "https://api.hr-ebnhaian.cloud/api/v1";

const nationalityOptions = Object.values(
  countries.getNames("en", { select: "official" })
);

const ROLE_TYPES = ["DOCTOR", "NURSE", "TECHNICIAN"];
const LICENSE_AUTHORITIES = ["DHA", "MOH", "DOH"];
const STAFF_STATUS = ["ACTIVE", "ON_LEAVE", "INACTIVE", "TERMINATED"];

const SALARY_TYPES = ["FIXED", "HOURLY", "COMMISSION"];
const COMMISSION_TYPES = ["PERCENTAGE", "FIXED"];
const PAYMENT_METHODS = ["CASH", "BANK_TRANSFER", "WPS"];
const CONTRACT_TYPES = ["FULL_TIME", "PART_TIME", "VISITING"];

const Section = ({ title, children }) => (
  <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
    <Typography fontWeight="bold">{title}</Typography>
    <Divider sx={{ my: 2 }} />
    {children}
  </Paper>
);

export default function AddMedicalStaff() {
  const [centers, setCenters] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [preview, setPreview] = useState(null);

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    axios.get(`${API_BASE_URL}/centers`).then((res) => {
      setCenters(res.data?.data || []);
    });
    axios.get(`${API_BASE_URL}/departments`).then((res) => {
      setDepartments(res.data?.data || []);
    });
  }, []);

  // ✅ Required fields list (for stars + force touched on submit)
  const REQUIRED_FIELDS = useMemo(
    () => [
      "firstName",
      "lastName",
      "email",
      "phone",
      "roleType",
      "center",
      "department",
      "startDate",
      "medicalLicenses",
      "salary.basicSalary",
      "contractType",
      "workingDaysPerWeek",
      "workingHoursPerDay",
    ],
    []
  );

  const isRequired = (path) => REQUIRED_FIELDS.includes(path);

  // ✅ helper for nested touched/errors
  const fieldError = (path) => {
    const t = getIn(formik.touched, path);
    const e = getIn(formik.errors, path);
    return { error: Boolean(t && e), helperText: t && e ? e : "" };
  };

  // ✅ Schema: documents files are OPTIONAL (passport/residence/visa/emirates/contract optional)
  const schema = Yup.object({
    firstName: Yup.string().required("Required"),
    lastName: Yup.string().required("Required"),
    email: Yup.string().email("Invalid email").required("Required"),
    phone: Yup.string().required("Required"),

    roleType: Yup.string().required("Required"),
    center: Yup.string().required("Required"),
    department: Yup.string().required("Required"),
    startDate: Yup.string().required("Required"),

    medicalLicenses: Yup.array()
      .of(
        Yup.object({
          authority: Yup.string().required("Authority is required"),
          licenseNumber: Yup.string().required("License number is required"),
          issueDate: Yup.string().required("Issue date is required"),
          expiryDate: Yup.string().required("Expiry date is required"),
          file: Yup.mixed().nullable(), // ✅ optional
        })
      )
      .min(1, "At least one medical license is required"),

    salary: Yup.object({
      basicSalary: Yup.number().typeError("Must be a number").required("Required"),
    }),

    contractType: Yup.string().required("Required"),

    workingDaysPerWeek: Yup.number().min(1, "Min 1").required("Required"),
    workingHoursPerDay: Yup.number().min(1, "Min 1").required("Required"),

    // ✅ files optional
    passportFile: Yup.mixed().nullable(),
    residenceFile: Yup.mixed().nullable(),
    visaFile: Yup.mixed().nullable(),
    emiratesIdFile: Yup.mixed().nullable(),
    contractFile: Yup.mixed().nullable(),
    profileImage: Yup.mixed().nullable(),
  });

  const formik = useFormik({
    initialValues: {
      // ===== PERSONAL =====
      firstName: "",
      fatherName: "",
      lastName: "",
      birthDate: "",
      nationality: "",
      gender: "",
      email: "",
      phone: "",
      address: "",
      profileImage: null,

      // ===== PROFESSIONAL =====
      roleType: "DOCTOR",
      specialization: "",
      center: "",
      department: "",
      startDate: "",
      endDate: "",
      status: "ACTIVE",

      // ===== LICENSES =====
      medicalLicenses: [
        {
          authority: "DHA",
          licenseNumber: "",
          issueDate: "",
          expiryDate: "",
          file: null,
        },
      ],

      // ===== DOCUMENTS =====
      passportNumber: "",
      passportIssueDate: "",
      passportExpiryDate: "",
      passportFile: null,

      residenceNumber: "",
      residenceIssueDate: "",
      residenceExpiryDate: "",
      residenceFile: null,

      visaNumber: "",
      visaIssueDate: "",
      visaExpiryDate: "",
      visaFile: null,

      emiratesIdNumber: "",
      emiratesIdExpiryDate: "",
      emiratesIdFile: null,

      // ===== SALARY & CONTRACT =====
      salaryType: "FIXED",
      salary: {
        basicSalary: "",
        hourlyRate: "",
        workingDaysPerMonth: 30,
        allowances: { housing: 0, transport: 0, other: 0 },
        overtime: { enabled: false, ratePerHour: 0 },
        deductions: { latePenaltyPerHour: 0, other: 0 },
        paymentMethod: "BANK_TRANSFER",
        commission: { type: "PERCENTAGE", value: "" },
      },

      contractType: "FULL_TIME",
      contractStartDate: "",
      contractEndDate: "",
      contractFile: null,

      // ===== ATTENDANCE & LEAVES =====
      workingDaysPerWeek: 6,
      workingHoursPerDay: 8,
      annualLeave: 30,
      sickLeave: 0,
      unpaidLeave: 0,

      // ===== NOTES =====
      notes: "",
    },

    validationSchema: schema,

    onSubmit: async (values, { setSubmitting, setFieldError, resetForm }) => {
      try {
        const fd = new FormData();

        const flatKeys = [
          "firstName",
          "fatherName",
          "lastName",
          "birthDate",
          "nationality",
          "gender",
          "email",
          "phone",
          "address",
          "roleType",
          "specialization",
          "center",
          "department",
          "startDate",
          "endDate",
          "status",
          "passportNumber",
          "passportIssueDate",
          "passportExpiryDate",
          "residenceNumber",
          "residenceIssueDate",
          "residenceExpiryDate",
          "visaNumber",
          "visaIssueDate",
          "visaExpiryDate",
          "emiratesIdNumber",
          "emiratesIdExpiryDate",
          "contractType",
          "contractStartDate",
          "contractEndDate",
          "workingDaysPerWeek",
          "workingHoursPerDay",
          "annualLeave",
          "sickLeave",
          "unpaidLeave",
          "notes",
        ];

        flatKeys.forEach((key) => {
          const value = values[key];
          if (value !== null && value !== "" && value !== undefined) {
            fd.append(key, value);
          }
        });

        // ✅ FILES (OPTIONAL)
        if (values.profileImage) fd.append("profileImage", values.profileImage);
        if (values.passportFile) fd.append("passportFile", values.passportFile);
        if (values.residenceFile) fd.append("residenceFile", values.residenceFile);
        if (values.visaFile) fd.append("visaFile", values.visaFile);
        if (values.emiratesIdFile) fd.append("emiratesIdFile", values.emiratesIdFile);
        if (values.contractFile) fd.append("contractFile", values.contractFile);

        // ✅ COMMISSION (your backend requires it)
        const basicSalary = values.salary?.basicSalary ?? 0;
        fd.append("commission[type]", "FIXED");
        fd.append("commission[value]", String(basicSalary));
        fd.append("commission[applyOn]", "ALL");

        // ✅ LICENSES
        values.medicalLicenses.forEach((lic, i) => {
          fd.append(
            `medicalLicenses[${i}][authority]`,
            String(lic.authority).trim().toUpperCase()
          );
          fd.append(
            `medicalLicenses[${i}][licenseNumber]`,
            String(lic.licenseNumber).trim()
          );
          fd.append(`medicalLicenses[${i}][issueDate]`, lic.issueDate);
          fd.append(`medicalLicenses[${i}][expiryDate]`, lic.expiryDate);

          if (lic.file) {
            fd.append(`medicalLicenseFile_${i}`, lic.file);
          }
        });

        await axios.post(`${API_BASE_URL}/doctors`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        setSnack({
          open: true,
          message: "تمت إضافة الطبيب بنجاح ✅",
          severity: "success",
        });

        resetForm();
        setPreview(null);
      } catch (err) {
        const msg = err.response?.data?.message || "";

        if (msg.toLowerCase().includes("phone")) {
          setFieldError("phone", "رقم الموبايل مستخدم مسبقًا");
          setSnack({ open: true, message: "رقم الموبايل مستخدم مسبقًا", severity: "error" });
        } else if (msg.toLowerCase().includes("email")) {
          setFieldError("email", "الإيميل مستخدم مسبقًا");
          setSnack({ open: true, message: "الإيميل مستخدم مسبقًا", severity: "error" });
        } else {
          setSnack({
            open: true,
            message: "حدث خطأ غير متوقع، يرجى المحاولة لاحقًا",
            severity: "error",
          });
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  // ✅ touch all required fields (including nested) to show errors on Save
  const touchAllRequired = () => {
    const touched = { ...formik.touched };

    const setTouchedPath = (obj, path) => {
      const parts = path.split(".");
      let cur = obj;
      for (let i = 0; i < parts.length; i++) {
        const p = parts[i];
        if (i === parts.length - 1) {
          cur[p] = true;
        } else {
          cur[p] = cur[p] || {};
          cur = cur[p];
        }
      }
    };

    REQUIRED_FIELDS.forEach((p) => setTouchedPath(touched, p));

    // licenses fields too (so errors show inside)
    if (Array.isArray(formik.values.medicalLicenses)) {
      touched.medicalLicenses = formik.values.medicalLicenses.map(() => ({
        authority: true,
        licenseNumber: true,
        issueDate: true,
        expiryDate: true,
        file: true,
      }));
    }

    formik.setTouched(touched, true);
    formik.handleSubmit();
  };

  return (
    <Box p={3} sx={{ background: "#f4f6fa", minHeight: "100vh" }}>
      <Typography variant="h5" fontWeight="bold" mb={2}>
        Add Medical Staff
      </Typography>

      <Paper sx={{ mb: 3, p: 2, borderRadius: 3 }}>
        <Typography fontWeight="bold">
          All fields in one page — required fields marked with *
        </Typography>
      </Paper>

      <form onSubmit={formik.handleSubmit}>
        {/* ================= PERSONAL ================= */}
        <Section title="Personal Information">
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Avatar
                src={preview}
                sx={{ width: 120, height: 120, border: "3px solid #1976d2" }}
              >
                {!preview && formik.values.firstName?.[0]}
              </Avatar>

              <Button component="label" size="small" sx={{ mt: 1 }}>
                Upload Image
                <input
                  hidden
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    setPreview(URL.createObjectURL(f));
                    formik.setFieldValue("profileImage", f);
                  }}
                />
              </Button>
            </Grid>

            {[
              ["firstName", "First Name"],
              ["fatherName", "Second Name (Father)"],
              ["lastName", "Third Name (Last)"],
              ["email", "Email"],
            ].map(([k, l]) => (
              <Grid item xs={12} md={3} key={k}>
                <TextField
                  label={l}
                  fullWidth
                  required={isRequired(k)}
                  value={formik.values[k]}
                  onChange={formik.handleChange}
                  name={k}
                  error={formik.touched[k] && Boolean(formik.errors[k])}
                  helperText={formik.touched[k] && formik.errors[k]}
                />
              </Grid>
            ))}

            <Grid item xs={12} md={3}>
              <TextField
                type="date"
                label="Birth Date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                name="birthDate"
                value={formik.values.birthDate}
                onChange={formik.handleChange}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                select
                label="Gender"
                fullWidth
                name="gender"
                value={formik.values.gender}
                onChange={formik.handleChange}
              >
                <MenuItem value="MALE">Male</MenuItem>
                <MenuItem value="FEMALE">Female</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} md={3}>
              <Autocomplete
                options={nationalityOptions}
                value={formik.values.nationality || null}
                onChange={(e, v) => formik.setFieldValue("nationality", v || "")}
                renderInput={(params) => (
                  <TextField {...params} label="Nationality" fullWidth name="nationality" />
                )}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <Box>
                <Typography variant="caption">
                  Phone {isRequired("phone") ? "*" : ""}
                </Typography>
                <PhoneInput
                  country="ae"
                  value={formik.values.phone}
                  onChange={(v) => formik.setFieldValue("phone", v)}
                  onBlur={() => formik.setFieldTouched("phone", true)}
                  inputStyle={{ width: "100%" }}
                />
                {formik.touched.phone && formik.errors.phone && (
                  <Typography color="error" fontSize={12} mt={0.5}>
                    {formik.errors.phone}
                  </Typography>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Address"
                fullWidth
                name="address"
                value={formik.values.address}
                onChange={formik.handleChange}
              />
            </Grid>
          </Grid>
        </Section>

        {/* ================= PROFESSIONAL ================= */}
        <Section title="Professional Information">
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                select
                label="Type"
                fullWidth
                required={isRequired("roleType")}
                name="roleType"
                value={formik.values.roleType}
                onChange={formik.handleChange}
                error={formik.touched.roleType && Boolean(formik.errors.roleType)}
                helperText={formik.touched.roleType && formik.errors.roleType}
              >
                {ROLE_TYPES.map((r) => (
                  <MenuItem key={r} value={r}>
                    {r}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                label="Specialization"
                fullWidth
                name="specialization"
                value={formik.values.specialization}
                onChange={formik.handleChange}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                select
                label="Center"
                fullWidth
                required={isRequired("center")}
                name="center"
                value={formik.values.center}
                onChange={formik.handleChange}
                error={formik.touched.center && Boolean(formik.errors.center)}
                helperText={formik.touched.center && formik.errors.center}
              >
                {centers.map((c) => (
                  <MenuItem key={c._id} value={c._id}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                select
                label="Department"
                fullWidth
                required={isRequired("department")}
                name="department"
                value={formik.values.department}
                onChange={formik.handleChange}
                error={formik.touched.department && Boolean(formik.errors.department)}
                helperText={formik.touched.department && formik.errors.department}
              >
                {departments.map((d) => (
                  <MenuItem key={d._id} value={d._id}>
                    {d.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                type="date"
                label="Start Date"
                fullWidth
                required={isRequired("startDate")}
                InputLabelProps={{ shrink: true }}
                name="startDate"
                value={formik.values.startDate}
                onChange={formik.handleChange}
                error={formik.touched.startDate && Boolean(formik.errors.startDate)}
                helperText={formik.touched.startDate && formik.errors.startDate}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                type="date"
                label="End Date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                name="endDate"
                value={formik.values.endDate}
                onChange={formik.handleChange}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                select
                label="Status"
                fullWidth
                name="status"
                value={formik.values.status}
                onChange={formik.handleChange}
              >
                {STAFF_STATUS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Section>

        {/* ================= LICENSES ================= */}
        <Section title="Licenses (يمكن إضافة أكثر من ترخيص)">
          {formik.values.medicalLicenses.map((lic, index) => (
            <Paper
              key={index}
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 2,
                border: "1px dashed #c9d1e1",
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography fontWeight="bold">License #{index + 1}</Typography>

                {formik.values.medicalLicenses.length > 1 && (
                  <IconButton
                    color="error"
                    onClick={() => {
                      const copy = [...formik.values.medicalLicenses];
                      copy.splice(index, 1);
                      formik.setFieldValue("medicalLicenses", copy);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField
                    select
                    label="Authority"
                    fullWidth
                    required
                    value={lic.authority}
                    onChange={(e) =>
                      formik.setFieldValue(`medicalLicenses.${index}.authority`, e.target.value)
                    }
                    {...fieldError(`medicalLicenses.${index}.authority`)}
                  >
                    {LICENSE_AUTHORITIES.map((a) => (
                      <MenuItem key={a} value={a}>
                        {a}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    label="License Number"
                    fullWidth
                    required
                    value={lic.licenseNumber}
                    onChange={(e) =>
                      formik.setFieldValue(`medicalLicenses.${index}.licenseNumber`, e.target.value)
                    }
                    {...fieldError(`medicalLicenses.${index}.licenseNumber`)}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    type="date"
                    label="Issue Date"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    required
                    value={lic.issueDate}
                    onChange={(e) =>
                      formik.setFieldValue(`medicalLicenses.${index}.issueDate`, e.target.value)
                    }
                    {...fieldError(`medicalLicenses.${index}.issueDate`)}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    type="date"
                    label="Expiry Date"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    required
                    value={lic.expiryDate}
                    onChange={(e) =>
                      formik.setFieldValue(`medicalLicenses.${index}.expiryDate`, e.target.value)
                    }
                    {...fieldError(`medicalLicenses.${index}.expiryDate`)}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Button component="label" variant="outlined" fullWidth>
                    Upload License File (Optional)
                    <input
                      hidden
                      type="file"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        formik.setFieldValue(`medicalLicenses.${index}.file`, f || null);
                      }}
                    />
                  </Button>

                  {lic.file && (
                    <Typography fontSize={12} mt={0.5}>
                      {lic.file.name}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Paper>
          ))}

          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            onClick={() => {
              formik.setFieldValue("medicalLicenses", [
                ...formik.values.medicalLicenses,
                {
                  authority: "DHA",
                  licenseNumber: "",
                  issueDate: "",
                  expiryDate: "",
                  file: null,
                },
              ]);
            }}
          >
            Add Another License
          </Button>

          {formik.touched.medicalLicenses && formik.errors.medicalLicenses && (
            <Typography color="error" fontSize={12} mt={1}>
              {typeof formik.errors.medicalLicenses === "string"
                ? formik.errors.medicalLicenses
                : "Please check licenses fields"}
            </Typography>
          )}
        </Section>

        {/* ================= DOCUMENTS ================= */}
        <Section title="Documents (Passport / Residence / Visa / Emirates ID) — Optional Files">
          {/* Passport */}
          <Typography fontWeight="bold" mb={1}>
            Passport
          </Typography>
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} md={3}>
              <TextField
                label="Passport Number"
                fullWidth
                name="passportNumber"
                value={formik.values.passportNumber}
                onChange={formik.handleChange}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                type="date"
                label="Issue Date"
                InputLabelProps={{ shrink: true }}
                fullWidth
                name="passportIssueDate"
                value={formik.values.passportIssueDate}
                onChange={formik.handleChange}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                type="date"
                label="Expiry Date"
                InputLabelProps={{ shrink: true }}
                fullWidth
                name="passportExpiryDate"
                value={formik.values.passportExpiryDate}
                onChange={formik.handleChange}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button component="label" variant="outlined" fullWidth>
                Upload Passport (Optional)
                <input
                  hidden
                  type="file"
                  onChange={(e) =>
                    formik.setFieldValue("passportFile", e.target.files?.[0] || null)
                  }
                />
              </Button>
              {formik.values.passportFile && (
                <Typography fontSize={12} mt={0.5}>
                  {formik.values.passportFile.name}
                </Typography>
              )}
            </Grid>
          </Grid>

          {/* Residence */}
          <Typography fontWeight="bold" mb={1}>
            Residence
          </Typography>
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} md={3}>
              <TextField
                label="Residence Number"
                fullWidth
                name="residenceNumber"
                value={formik.values.residenceNumber}
                onChange={formik.handleChange}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                type="date"
                label="Issue Date"
                InputLabelProps={{ shrink: true }}
                fullWidth
                name="residenceIssueDate"
                value={formik.values.residenceIssueDate}
                onChange={formik.handleChange}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                type="date"
                label="Expiry Date"
                InputLabelProps={{ shrink: true }}
                fullWidth
                name="residenceExpiryDate"
                value={formik.values.residenceExpiryDate}
                onChange={formik.handleChange}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button component="label" variant="outlined" fullWidth>
                Upload Residence (Optional)
                <input
                  hidden
                  type="file"
                  onChange={(e) =>
                    formik.setFieldValue("residenceFile", e.target.files?.[0] || null)
                  }
                />
              </Button>
              {formik.values.residenceFile && (
                <Typography fontSize={12} mt={0.5}>
                  {formik.values.residenceFile.name}
                </Typography>
              )}
            </Grid>
          </Grid>

          {/* Visa */}
          <Typography fontWeight="bold" mb={1}>
            Visa
          </Typography>
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} md={3}>
              <TextField
                label="Visa Number"
                fullWidth
                name="visaNumber"
                value={formik.values.visaNumber}
                onChange={formik.handleChange}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                type="date"
                label="Issue Date"
                InputLabelProps={{ shrink: true }}
                fullWidth
                name="visaIssueDate"
                value={formik.values.visaIssueDate}
                onChange={formik.handleChange}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                type="date"
                label="Expiry Date"
                InputLabelProps={{ shrink: true }}
                fullWidth
                name="visaExpiryDate"
                value={formik.values.visaExpiryDate}
                onChange={formik.handleChange}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button component="label" variant="outlined" fullWidth>
                Upload Visa (Optional)
                <input
                  hidden
                  type="file"
                  onChange={(e) => formik.setFieldValue("visaFile", e.target.files?.[0] || null)}
                />
              </Button>
              {formik.values.visaFile && (
                <Typography fontSize={12} mt={0.5}>
                  {formik.values.visaFile.name}
                </Typography>
              )}
            </Grid>
          </Grid>

          {/* Emirates ID */}
          <Typography fontWeight="bold" mb={1}>
            Emirates ID
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                label="Emirates ID Number"
                fullWidth
                name="emiratesIdNumber"
                value={formik.values.emiratesIdNumber}
                onChange={formik.handleChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                type="date"
                label="Expiry Date"
                InputLabelProps={{ shrink: true }}
                fullWidth
                name="emiratesIdExpiryDate"
                value={formik.values.emiratesIdExpiryDate}
                onChange={formik.handleChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button component="label" variant="outlined" fullWidth>
                Upload Emirates ID (Optional)
                <input
                  hidden
                  type="file"
                  onChange={(e) =>
                    formik.setFieldValue("emiratesIdFile", e.target.files?.[0] || null)
                  }
                />
              </Button>
              {formik.values.emiratesIdFile && (
                <Typography fontSize={12} mt={0.5}>
                  {formik.values.emiratesIdFile.name}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Section>

        {/* ================= SALARY & CONTRACT ================= */}
        <Section title="Salary & Contract">
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                select
                label="Salary Type"
                fullWidth
                name="salaryType"
                value={formik.values.salaryType}
                onChange={formik.handleChange}
              >
                {SALARY_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* basic salary */}
            <Grid item xs={12} md={3}>
              <TextField
                label="Basic Salary"
                type="number"
                fullWidth
                required={isRequired("salary.basicSalary")}
                value={formik.values.salary?.basicSalary ?? ""}
                onChange={(e) => formik.setFieldValue("salary.basicSalary", e.target.value)}
                {...fieldError("salary.basicSalary")}
              />
            </Grid>

            {formik.values.salaryType === "HOURLY" && (
              <Grid item xs={12} md={3}>
                <TextField
                  label="Hourly Rate"
                  type="number"
                  fullWidth
                  value={formik.values.salary?.hourlyRate ?? ""}
                  onChange={(e) => formik.setFieldValue("salary.hourlyRate", e.target.value)}
                />
              </Grid>
            )}

            {formik.values.salaryType === "COMMISSION" && (
              <>
                <Grid item xs={12} md={3}>
                  <TextField
                    select
                    label="Commission Type"
                    fullWidth
                    value={formik.values.salary?.commission?.type ?? "PERCENTAGE"}
                    onChange={(e) => formik.setFieldValue("salary.commission.type", e.target.value)}
                  >
                    {COMMISSION_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>
                        {t}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Commission Value"
                    type="number"
                    fullWidth
                    value={formik.values.salary?.commission?.value ?? ""}
                    onChange={(e) => formik.setFieldValue("salary.commission.value", e.target.value)}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} md={3}>
              <TextField
                label="Working Days / Month"
                type="number"
                fullWidth
                value={formik.values.salary?.workingDaysPerMonth ?? 30}
                onChange={(e) => formik.setFieldValue("salary.workingDaysPerMonth", e.target.value)}
              />
            </Grid>

            {/* Allowances */}
            <Grid item xs={12} md={3}>
              <TextField
                label="Housing Allowance"
                type="number"
                fullWidth
                value={formik.values.salary?.allowances?.housing ?? 0}
                onChange={(e) => formik.setFieldValue("salary.allowances.housing", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Transport Allowance"
                type="number"
                fullWidth
                value={formik.values.salary?.allowances?.transport ?? 0}
                onChange={(e) => formik.setFieldValue("salary.allowances.transport", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Other Allowance"
                type="number"
                fullWidth
                value={formik.values.salary?.allowances?.other ?? 0}
                onChange={(e) => formik.setFieldValue("salary.allowances.other", e.target.value)}
              />
            </Grid>

            {/* Overtime */}
            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(formik.values.salary?.overtime?.enabled)}
                    onChange={(e) =>
                      formik.setFieldValue("salary.overtime.enabled", e.target.checked)
                    }
                  />
                }
                label="Overtime Enabled"
              />
            </Grid>

            {formik.values.salary?.overtime?.enabled && (
              <Grid item xs={12} md={3}>
                <TextField
                  label="Overtime Rate / Hour"
                  type="number"
                  fullWidth
                  value={formik.values.salary?.overtime?.ratePerHour ?? 0}
                  onChange={(e) => formik.setFieldValue("salary.overtime.ratePerHour", e.target.value)}
                />
              </Grid>
            )}

            {/* Deductions */}
            <Grid item xs={12} md={3}>
              <TextField
                label="Late Penalty / Hour"
                type="number"
                fullWidth
                value={formik.values.salary?.deductions?.latePenaltyPerHour ?? 0}
                onChange={(e) =>
                  formik.setFieldValue("salary.deductions.latePenaltyPerHour", e.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Other Deduction"
                type="number"
                fullWidth
                value={formik.values.salary?.deductions?.other ?? 0}
                onChange={(e) => formik.setFieldValue("salary.deductions.other", e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                select
                label="Payment Method"
                fullWidth
                value={formik.values.salary?.paymentMethod ?? "BANK_TRANSFER"}
                onChange={(e) => formik.setFieldValue("salary.paymentMethod", e.target.value)}
              >
                {PAYMENT_METHODS.map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Contract */}
            <Grid item xs={12} md={3}>
              <TextField
                select
                label="Contract Type"
                fullWidth
                required={isRequired("contractType")}
                name="contractType"
                value={formik.values.contractType}
                onChange={formik.handleChange}
                error={formik.touched.contractType && Boolean(formik.errors.contractType)}
                helperText={formik.touched.contractType && formik.errors.contractType}
              >
                {CONTRACT_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                type="date"
                label="Contract Start"
                InputLabelProps={{ shrink: true }}
                fullWidth
                name="contractStartDate"
                value={formik.values.contractStartDate}
                onChange={formik.handleChange}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                type="date"
                label="Contract End"
                InputLabelProps={{ shrink: true }}
                fullWidth
                name="contractEndDate"
                value={formik.values.contractEndDate}
                onChange={formik.handleChange}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <Button component="label" variant="outlined" fullWidth>
                Upload Contract File (Optional)
                <input
                  hidden
                  type="file"
                  onChange={(e) =>
                    formik.setFieldValue("contractFile", e.target.files?.[0] || null)
                  }
                />
              </Button>
              {formik.values.contractFile && (
                <Typography fontSize={12} mt={0.5}>
                  {formik.values.contractFile.name}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Section>

        {/* ================= ATTENDANCE & LEAVES ================= */}
        <Section title="Attendance & Leaves">
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                label="Working Days / Week"
                type="number"
                fullWidth
                required={isRequired("workingDaysPerWeek")}
                name="workingDaysPerWeek"
                value={formik.values.workingDaysPerWeek}
                onChange={formik.handleChange}
                error={formik.touched.workingDaysPerWeek && Boolean(formik.errors.workingDaysPerWeek)}
                helperText={formik.touched.workingDaysPerWeek && formik.errors.workingDaysPerWeek}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                label="Working Hours / Day"
                type="number"
                fullWidth
                required={isRequired("workingHoursPerDay")}
                name="workingHoursPerDay"
                value={formik.values.workingHoursPerDay}
                onChange={formik.handleChange}
                error={formik.touched.workingHoursPerDay && Boolean(formik.errors.workingHoursPerDay)}
                helperText={formik.touched.workingHoursPerDay && formik.errors.workingHoursPerDay}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                label="Annual Leave"
                type="number"
                fullWidth
                name="annualLeave"
                value={formik.values.annualLeave}
                onChange={formik.handleChange}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                label="Sick Leave"
                type="number"
                fullWidth
                name="sickLeave"
                value={formik.values.sickLeave}
                onChange={formik.handleChange}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                label="Unpaid Leave"
                type="number"
                fullWidth
                name="unpaidLeave"
                value={formik.values.unpaidLeave}
                onChange={formik.handleChange}
              />
            </Grid>
          </Grid>
        </Section>

        {/* ================= NOTES ================= */}
        <Section title="Notes">
          <TextField
            label="Notes"
            fullWidth
            multiline
            rows={4}
            name="notes"
            value={formik.values.notes}
            onChange={formik.handleChange}
          />
        </Section>

        {/* ================= SAVE ================= */}
        <Box mt={2} display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            color="success"
            type="button"
            onClick={touchAllRequired}
            disabled={formik.isSubmitting}
          >
            Save
          </Button>
        </Box>
      </form>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack.severity}
          variant="filled"
          onClose={() => setSnack({ ...snack, open: false })}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}